/**
 * Typed error classes thrown by the API client.
 *
 * Components use `instanceof` checks rather than parsing error messages:
 *
 *     try { await apiFetch('/contracts/upload', ...) }
 *     catch (e) {
 *       if (e instanceof PlanGateError) { router.push('/subscribe') }
 *       else if (e instanceof RateLimitError) { await sleep(e.retryAfterMs) }
 *       else throw e
 *     }
 *
 * Source: auth.json (errors block) + audit phase 09 §8.
 */

/**
 * Backend error envelope.  `detail` is usually a string but the new
 * own-auth surface uses richer shapes:
 *   - 402 trial_expired/feature_not_in_plan → `{ error, redirect?, message?, feature? }`
 *   - 403 feature_not_assigned              → `{ error, feature, message? }`
 *   - 422 password_complexity / pydantic    → array of `{ loc, msg, type }`
 */
export type ApiErrorDetail = {
  detail?:
    | string
    | Array<{ loc?: string[]; msg?: string; type?: string }>
    | { error?: string; feature?: string; redirect?: string; message?: string };
  [key: string]: unknown;
};

/** Extract a human-readable string from any of the documented detail shapes. */
function detailString(body: ApiErrorDetail | null): string | null {
  const d = body?.detail;
  if (!d) return null;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    const first = d[0];
    return first?.msg ?? null;
  }
  return d.message ?? d.error ?? null;
}

/** Base class — catch-all for anything the API client throws. */
export class ApiError extends Error {
  readonly status: number;
  readonly body: ApiErrorDetail | null;
  readonly url: string;

  constructor(message: string, status: number, body: ApiErrorDetail | null, url: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    this.url = url;
  }
}

/** 401 — missing / expired JWT (auth.json:89-90). */
export class AuthError extends ApiError {
  constructor(body: ApiErrorDetail | null, url: string) {
    super(detailString(body) ?? 'Unauthenticated', 401, body, url);
    this.name = 'AuthError';
  }
}

/**
 * 402 — plan gate.  The new own-auth surface returns a structured detail
 * object: `{ error: 'trial_expired' | 'no_plan' | 'feature_not_in_plan'
 *                  | 'payment_method_required',
 *            redirect: '/subscribe', message?, feature? }`.
 *
 * `payment_method_required` appears only on deployments running the
 * card-required trial (backend `settings.require_card_for_trial`). It is NOT
 * `trial_expired`: the customer's time has not run out, it has not started,
 * so the destination is the card form rather than a "your trial ended"
 * notice.  Branch on `errorCode`, never on the message.
 *
 * `errorCode` and `requiredFeature` surface the structured shape so
 * components can branch without re-parsing the body.  Components should
 * route `redirect` users to `/subscribe`.
 */
export class PlanGateError extends ApiError {
  readonly errorCode: string | null;
  readonly requiredFeature: string | null;
  readonly redirect: string | null;
  /** Absolute, edition-correct upgrade link when the API supplies one. */
  readonly upgradeUrl: string | null;
  /** Quota context, when the gate is a quota rather than a feature. */
  readonly limit: number | null;
  readonly used: number | null;

  constructor(body: ApiErrorDetail | null, url: string) {
    super(detailString(body) ?? 'Plan upgrade required', 402, body, url);
    this.name = 'PlanGateError';

    // The API speaks 402 in two shapes and this class understood only one.
    //
    //   nested — `raise HTTPException(402, detail={...})` in dependencies.py
    //            (subscription and feature gates) renders as
    //            {"detail": {"error": ..., "redirect": ...}}
    //   flat   — the quota handlers in main.py return a JSONResponse whose
    //            fields sit at the top level, with `detail` a plain sentence:
    //            {"error": ..., "detail": "...", "redirect": ..., ...}
    //
    // Reading `detail` alone meant every quota refusal — contracts, AI
    // tokens, daily spend, storage — arrived with errorCode null and was
    // sent to `/subscribe` regardless of where the API asked it to go.
    //
    // This is the same defect the India app carried; both were written from
    // the nested shape before the quota handlers existed. Keep the two files
    // in step: they are separate apps, but they answer the same API.
    const nested =
      body?.detail && typeof body.detail === 'object' && !Array.isArray(body.detail)
        ? body.detail
        : null;
    const flat = (body ?? {}) as Record<string, unknown>;

    const pick = <T>(key: string): T | null =>
      ((nested as Record<string, unknown> | null)?.[key] as T | undefined) ??
      (flat[key] as T | undefined) ??
      null;

    this.errorCode = pick<string>('error');
    this.requiredFeature = pick<string>('feature');
    // Only the quota shape carries these.
    this.upgradeUrl = pick<string>('upgrade_url');
    this.limit = pick<number>('limit');
    this.used = pick<number>('used');
    // `/subscribe` stays the fallback for a body that names no destination.
    this.redirect = pick<string>('redirect') ?? '/subscribe';
  }
}

/**
 * 403 — role gate or `feature_not_assigned` (corporate-org case where the
 * org has the feature but the individual user wasn't granted it).
 * Inspect `errorCode === 'feature_not_assigned'` to render
 * "Ask your admin to enable this".
 */
export class ForbiddenError extends ApiError {
  readonly errorCode: string | null;
  readonly requiredFeature: string | null;

  constructor(body: ApiErrorDetail | null, url: string) {
    super(detailString(body) ?? 'Forbidden', 403, body, url);
    this.name = 'ForbiddenError';
    const d = body?.detail;
    if (d && typeof d === 'object' && !Array.isArray(d)) {
      this.errorCode = d.error ?? null;
      this.requiredFeature = d.feature ?? null;
    } else {
      this.errorCode = null;
      this.requiredFeature = null;
    }
  }
}

/** 404 — resource not found (always org-scoped; another org's data is 404, not 403). */
export class NotFoundError extends ApiError {
  constructor(body: ApiErrorDetail | null, url: string) {
    super(detailString(body) ?? 'Not found', 404, body, url);
    this.name = 'NotFoundError';
  }
}

/** 409 — conflict (idempotency-key reuse with different body, state-machine violation, …). */
export class ConflictError extends ApiError {
  constructor(body: ApiErrorDetail | null, url: string) {
    super(detailString(body) ?? 'Conflict', 409, body, url);
    this.name = 'ConflictError';
  }
}

/** 410 — expired resource (share link past TTL, chunked-upload session). */
export class GoneError extends ApiError {
  constructor(body: ApiErrorDetail | null, url: string) {
    super(detailString(body) ?? 'Resource expired', 410, body, url);
    this.name = 'GoneError';
  }
}

/** 429 — rate-limited.  Client backs off for `retryAfterMs`. */
export class RateLimitError extends ApiError {
  readonly retryAfterMs: number;
  constructor(body: ApiErrorDetail | null, url: string) {
    super(detailString(body) ?? 'Rate limit exceeded', 429, body, url);
    this.name = 'RateLimitError';
    const secs =
      typeof body?.['retry_after_seconds'] === 'number' ? body['retry_after_seconds'] : 2;
    // Floor at 100 ms — defence against a misconfigured upstream sending 0,
    // which would collapse into a zero-backoff retry loop (F4-04).
    this.retryAfterMs = Math.max(100, secs * 1000);
  }
}

/** 422 — validation failure (FastAPI / Pydantic default shape). */
export class ValidationError extends ApiError {
  /** First field-level message, useful for inline form errors. */
  readonly fieldMessage: string | null;
  /** Field path (e.g. ['body', 'password']) of the first error. */
  readonly fieldPath: string[] | null;

  constructor(body: ApiErrorDetail | null, url: string) {
    super(detailString(body) ?? 'Validation error', 422, body, url);
    this.name = 'ValidationError';
    const d = body?.detail;
    if (Array.isArray(d) && d[0]) {
      this.fieldMessage = d[0].msg ?? null;
      this.fieldPath = d[0].loc ?? null;
    } else {
      this.fieldMessage = null;
      this.fieldPath = null;
    }
  }
}

/** 413 — file too large / quota exceeded by payload size. */
export class PayloadTooLargeError extends ApiError {
  constructor(body: ApiErrorDetail | null, url: string) {
    super(detailString(body) ?? 'Payload too large', 413, body, url);
    this.name = 'PayloadTooLargeError';
  }
}

/** 5xx — transient server / upstream failure.  Safe to retry with backoff. */
export class ServerError extends ApiError {
  constructor(body: ApiErrorDetail | null, status: number, url: string) {
    super(detailString(body) ?? `Server error (HTTP ${status})`, status, body, url);
    this.name = 'ServerError';
  }
}

/** Network-level failure (DNS, CORS, offline) — status is 0. */
export class NetworkError extends ApiError {
  override readonly cause: Error;
  constructor(cause: Error, url: string) {
    super(cause.message || 'Network error', 0, null, url);
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

/** Maps an HTTP status + body into the right error subclass. */
export function errorFromResponse(
  status: number,
  body: ApiErrorDetail | null,
  url: string,
): ApiError {
  switch (status) {
    case 401:
      return new AuthError(body, url);
    case 402:
      return new PlanGateError(body, url);
    case 403:
      return new ForbiddenError(body, url);
    case 404:
      return new NotFoundError(body, url);
    case 409:
      return new ConflictError(body, url);
    case 410:
      return new GoneError(body, url);
    case 413:
      return new PayloadTooLargeError(body, url);
    case 422:
      return new ValidationError(body, url);
    case 429:
      return new RateLimitError(body, url);
    default:
      return status >= 500
        ? new ServerError(body, status, url)
        : new ApiError(detailString(body) ?? `HTTP ${status}`, status, body, url);
  }
}
